import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Configuration for each filter field
 */
export interface FilterFieldConfig<T = any> {
  /** URL parameter key */
  key: string;
  /** Default value */
  defaultValue: T;
  /** Parse function to convert URL string to typed value */
  parse?: (value: string | null) => T;
  /** Serialize function to convert typed value to URL string */
  serialize?: (value: T) => string | null;
}

/**
 * Infer the filter state type from config
 */
type InferFiltersFromConfig<T extends Record<string, FilterFieldConfig>> = {
  [K in keyof T]: T[K] extends FilterFieldConfig<infer U> ? U : never;
};

/**
 * Custom hook for managing filters with URL persistence
 *
 * @example
 * ```tsx
 * const filterConfig = {
 *   studentName: {
 *     key: 'name',
 *     defaultValue: '',
 *     parse: (v) => v || '',
 *     serialize: (v) => v || null,
 *   },
 *   classId: {
 *     key: 'class',
 *     defaultValue: null as number | null,
 *     parse: (v) => v ? Number(v) : null,
 *     serialize: (v) => v ? String(v) : null,
 *   },
 * } as const;
 *
 * const { filters, setFilter, setFilters, clearFilters, applyFilters } =
 *   useUrlFilters(filterConfig);
 * ```
 */
export function useUrlFilters<T extends Record<string, FilterFieldConfig>>(
  config: T,
) {
  type Filters = InferFiltersFromConfig<T>;

  const [searchParams, setSearchParams] = useSearchParams();

  // Parse initial filters from URL or use defaults
  const parseFiltersFromUrl = useCallback((): Filters => {
    const filters = {} as Filters;

    for (const [fieldName, fieldConfig] of Object.entries(config)) {
      const urlValue = searchParams.get(fieldConfig.key);

      if (fieldConfig.parse) {
        filters[fieldName as keyof Filters] = fieldConfig.parse(
          urlValue,
        ) as any;
      } else {
        // Default parsing logic
        filters[fieldName as keyof Filters] = (urlValue ??
          fieldConfig.defaultValue) as any;
      }
    }

    return filters;
  }, [searchParams, config]);

  // Initialize state with URL params or defaults
  const [filters, setFiltersState] = useState<Filters>(parseFiltersFromUrl);

  // Sync filters to URL without navigation
  const syncFiltersToUrl = useCallback(
    (newFilters: Filters, replace: boolean = true) => {
      const newParams = new URLSearchParams(searchParams);

      for (const [fieldName, fieldConfig] of Object.entries(config)) {
        const value = newFilters[fieldName as keyof Filters];

        let urlValue: string | null;

        if (fieldConfig.serialize) {
          urlValue = fieldConfig.serialize(value);
        } else {
          // Default serialization logic
          if (value === null || value === undefined || value === "") {
            urlValue = null;
          } else {
            urlValue = String(value);
          }
        }

        if (urlValue) {
          newParams.set(fieldConfig.key, urlValue);
        } else {
          newParams.delete(fieldConfig.key);
        }
      }

      setSearchParams(newParams, { replace });
    },
    [config, searchParams, setSearchParams],
  );

  // Update URL when navigating back
  useEffect(() => {
    const urlFilters = parseFiltersFromUrl();
    setFiltersState(urlFilters);
  }, [searchParams, parseFiltersFromUrl]);

  // Set a single filter (without updating URL immediately)
  const setFilter = useCallback(
    <K extends keyof Filters>(key: K, value: Filters[K]) => {
      setFiltersState((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  // Set multiple filters (without updating URL immediately)
  const setFilters = useCallback((newFilters: Partial<Filters>) => {
    setFiltersState((prev) => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  // Apply filters: update URL and trigger search
  const applyFilters = useCallback(() => {
    syncFiltersToUrl(filters, false);
  }, [filters, syncFiltersToUrl]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    const defaultFilters = {} as Filters;

    for (const [fieldName, fieldConfig] of Object.entries(config)) {
      defaultFilters[fieldName as keyof Filters] =
        fieldConfig.defaultValue as any;
    }

    setFiltersState(defaultFilters);
    syncFiltersToUrl(defaultFilters, false);
  }, [config, syncFiltersToUrl]);

  // Check if any filter has a value
  const hasActiveFilters = useCallback((): boolean => {
    for (const [fieldName, fieldConfig] of Object.entries(config)) {
      const value = filters[fieldName as keyof Filters];
      const defaultValue = fieldConfig.defaultValue;

      if (
        value !== defaultValue &&
        value !== null &&
        value !== undefined &&
        value !== ""
      ) {
        return true;
      }
    }
    return false;
  }, [filters, config]);

  // Get URL-synced filters (from URL params)
  const getActiveFilters = useCallback((): Filters => {
    return parseFiltersFromUrl();
  }, [parseFiltersFromUrl]);

  return {
    /** Current filter state (may not be synced to URL yet) */
    filters,
    /** Set a single filter value */
    setFilter,
    /** Set multiple filter values */
    setFilters,
    /** Apply filters to URL (call this on search) */
    applyFilters,
    /** Clear all filters and update URL */
    clearFilters,
    /** Check if any filter is active */
    hasActiveFilters,
    /** Get filters that are currently in URL */
    getActiveFilters,
  };
}
